import React from "react";
import { Text, Heading, Button, Icon, Notification } from "@telia/teddy";
import { CUSTOMER_TYPE } from "../../../../../constants";
import { RootState } from "../../../../../models";
import { useSelector } from "react-redux";
import SubscriptionTextBlock from "../../../components/components/molecules/SubscriptionTextBlock/SubscriptionTextBlock";
import { saveTeliaPlaySummaryToPdf } from "../../../../utils/saveToPdf";
import ContactCard from "../../../checkout/components/molecules/ContactCard/ContactCard";
import * as CUSTOMER_INFO_SELECTORS from "../../../checkout/state/selectors/customerInfoSelectors";
import TEXT from "../../text-consts";
import { getSelectedPackage, isBroadbandCustomer } from "../../state/selectors";
import "./Summary.less";

const getMonthlySaving = (
    price?: { regularPrice: string; discountPrice: string } | null,
    undiscountedPrice?: { regularPrice: string; discountPrice: string } | null,
): number | undefined => {
    if (!price || !undiscountedPrice || !price.regularPrice.trim() || !undiscountedPrice.regularPrice.trim()) {
        return undefined;
    }

    const regularPrice = Number(price.regularPrice);
    const undiscountedRegularPrice = Number(undiscountedPrice.regularPrice);
    if (
        !Number.isFinite(regularPrice) ||
        !Number.isFinite(undiscountedRegularPrice) ||
        regularPrice < 0 ||
        undiscountedRegularPrice < 0
    ) {
        return undefined;
    }

    const monthlySaving = undiscountedRegularPrice - regularPrice;
    return monthlySaving > 0 ? monthlySaving : undefined;
};

const TeliaPlaySummary: React.FC = () => {
    const [showDetails, setShowDetails] = React.useState(false);
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [downloadError, setDownloadError] = React.useState(false);
    const selectedSubscription = useSelector((state: RootState) => state.teliaPlay?.selectedSubscription);
    const legalOwnerFullName = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.getLegalOwnerFullName(state));
    const legalOwnerCity = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.getLegalOwnerFullCity(state));
    const legalOwnerAddress = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.getLegalOwnerAddress(state));
    const contactInfo = useSelector((state: RootState) => state.teliaPlay?.personalDetails);
    const broadbandCustomer = useSelector(isBroadbandCustomer);
    const isExistingCustomer = useSelector(
        (state: RootState) => CUSTOMER_INFO_SELECTORS.getCurrentType(state) === CUSTOMER_TYPE.EXISTING,
    );
    const monthlySaving = useSelector((state: RootState) =>
        getMonthlySaving(
            getSelectedPackage(state)?.monthlyPrice,
            state.teliaPlay?.undiscountedSelectedPackage?.monthlyPrice,
        ),
    );
    const formattedMonthlySaving =
        monthlySaving !== undefined
            ? new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 2 }).format(monthlySaving)
            : undefined;

    const downloadSummary = async () => {
        if (!selectedSubscription) {
            throw new Error("Cannot download a Telia Play summary without a selected subscription");
        }
        setIsDownloading(true);
        setDownloadError(false);
        try {
            await saveTeliaPlaySummaryToPdf(selectedSubscription, {
                name: legalOwnerFullName,
                address: legalOwnerAddress,
                city: legalOwnerCity,
            });
        } catch {
            setDownloadError(true);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="summary-page">
            <Heading className="summary-page__heading" as="h1" variant="title-300">
                Oppsummering
            </Heading>
            {broadbandCustomer && (
                <Notification className="summary-page__customer-notification" variant="information">
                    <Notification.Icon name="info" />
                    <Notification.Text>
                        <Text as="p" variant="paragraph-100-bold">
                            {TEXT.SUMMARY.BROADBAND_CUSTOMER_TITLE}
                        </Text>
                        <Text as="p">{TEXT.SUMMARY.BROADBAND_CUSTOMER_DESCRIPTION}</Text>
                        <Button asChild variant="text">
                            <a href="https://www.telia.no/minside/">
                                {TEXT.SUMMARY.BROADBAND_CUSTOMER_LINK}
                                <Icon name="arrow-right" aria-hidden="true" />
                            </a>
                        </Button>
                    </Notification.Text>
                </Notification>
            )}
            {!broadbandCustomer && isExistingCustomer && formattedMonthlySaving && (
                <Notification className="summary-page__customer-notification" variant="success">
                    <Notification.Icon name="check-circle-filled" />
                    <Notification.Text>
                        <strong>{TEXT.SUMMARY.MOBILE_CUSTOMER_DISCOUNT_TITLE}</strong>{" "}
                        {TEXT.SUMMARY.MOBILE_CUSTOMER_DISCOUNT_DESCRIPTION(formattedMonthlySaving)}
                    </Notification.Text>
                </Notification>
            )}
            <div className="summary-page__summary-container">
                <section className="summary-page__customer" aria-labelledby="telia-play-customer-heading">
                    <Heading
                        id="telia-play-customer-heading"
                        className="summary-page__section-heading"
                        as="h2"
                        variant="subsection-100"
                    >
                        Kunde
                    </Heading>
                    <ContactCard
                        headingText=""
                        name={legalOwnerFullName}
                        address={legalOwnerAddress}
                        city={legalOwnerCity}
                        contactPhone={contactInfo?.phoneNumber}
                        contactEmail={contactInfo?.email}
                        showContactInfo={true}
                        showLogoutBankId={false}
                    />
                </section>
                {selectedSubscription && (
                    <>
                        <section
                            className="summary-page__subscription"
                            aria-labelledby="telia-play-subscription-heading"
                        >
                            <Heading
                                id="telia-play-subscription-heading"
                                className="summary-page__section-heading"
                                as="h2"
                                variant="subsection-100"
                            >
                                Ditt abonnement
                            </Heading>
                            <div className="summary-page__product-info">
                                <div>
                                    <Heading className="summary-page__product-heading" as="h3" variant="subsection-100">
                                        {selectedSubscription.subscriptionName}
                                    </Heading>
                                    <Text className="summary-page__ingress">
                                        {selectedSubscription.subscriptionShortname}
                                    </Text>
                                </div>
                                <div className="summary-page__product-price">
                                    <Text>{Number(selectedSubscription.monthlyPrice.regularPrice)},-/md.</Text>
                                </div>
                            </div>
                            {selectedSubscription.textBlocks.length > 0 && (
                                <>
                                    <Button
                                        className="summary-page__details-toggle"
                                        variant="text"
                                        type="button"
                                        aria-expanded={showDetails}
                                        aria-controls="telia-play-subscription-details"
                                        onClick={() => setShowDetails((previous) => !previous)}
                                    >
                                        Om abonnementet
                                        <Icon name={showDetails ? "chevron-up" : "chevron-down"} aria-hidden="true" />
                                    </Button>
                                    <div
                                        id="telia-play-subscription-details"
                                        className="summary-page__details"
                                        hidden={!showDetails}
                                    >
                                        {showDetails &&
                                            selectedSubscription.textBlocks.map((textBlock) => (
                                                <SubscriptionTextBlock key={textBlock.id} textBlock={textBlock} />
                                            ))}
                                    </div>
                                </>
                            )}
                        </section>
                        <div className="summary-page__price-container">
                            <Text variant="paragraph-100-bold">Per måned</Text>
                            <Text className="summary-page__total-price" variant="paragraph-100-bold">
                                {Number(selectedSubscription.monthlyPrice.regularPrice)},-/md.
                            </Text>
                        </div>
                        <Button
                            className="summary-page__download"
                            variant="text"
                            type="button"
                            disabled={isDownloading}
                            aria-busy={isDownloading}
                            onClick={downloadSummary}
                        >
                            <Icon name="document-pdf" aria-hidden="true" />
                            {isDownloading ? "Laster ned ordreoppsummering…" : "Last ned ordreoppsummering"}
                        </Button>
                        {downloadError && (
                            <div role="alert">
                                <Notification variant="error" type="tile">
                                    <Notification.Icon />
                                    <Notification.Text>
                                        Kunne ikke laste ned ordreoppsummeringen. Prøv igjen.
                                    </Notification.Text>
                                </Notification>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TeliaPlaySummary;
